import { useMemo, useState, useEffect, JSX } from 'react';
import {
  Modal,
  Group,
  Text,
  ScrollArea,
  Card,
  Button,
  ActionIcon,
  Box,
  Tooltip,
  SimpleGrid,
} from '@mantine/core';
import { IconArrowRight, IconGripVertical, IconEyeOff } from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface ColumnItem {
  id: string;
  label: string;
}

/**
 * SortableItem - сортируемая карточка колонки для правой панели (Отображаются)
 */
function SortableItem({
  item,
  onMoveLeft,
}: {
  item: ColumnItem;
  onMoveLeft: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={{ ...style, border: '1px solid var(--mantine-primary-color-filled)' }}
      p={6}
    >
      <Group justify='space-between' gap={4} wrap='nowrap'>
        <Group gap={4} wrap='nowrap' style={{ flex: 1, minWidth: 0 }}>
          <Tooltip label='Перетащить для изменения порядка'>
            <ActionIcon
              size='xs'
              variant='subtle'
              style={{ cursor: 'grab', touchAction: 'none' }}
              {...attributes}
              {...listeners}
              aria-label='Перетащить'
            >
              <IconGripVertical size={12} />
            </ActionIcon>
          </Tooltip>
          <Text size='xs' truncate style={{ flex: 1, fontSize: 11 }}>
            {item.label}
          </Text>
        </Group>
        <Tooltip label='Скрыть'>
          <ActionIcon size='xs' variant='subtle' onClick={onMoveLeft} aria-label='Скрыть'>
            <IconEyeOff size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

export interface ColumnSettingsModalProps {
  opened: boolean;
  onClose: () => void;
  tableId: string;
  /** Текущий порядок видимых колонок */
  visible: ColumnItem[];
  /** Список скрытых колонок */
  hidden: ColumnItem[];
  /** Применить изменения: передаём итоговый порядок видимых и список скрытых */
  onApply: (nextVisibleOrder: string[], nextHiddenIds: string[]) => void;
  /** Сбросить к дефолтному порядку. Возвращает списки видимых/скрытых ID для превью в модалке. */
  onReset: () => { visible: string[]; hidden: string[] } | void;
}

/**
 * Модалка настройки колонок таблицы.
 *
 * Позволяет:
 * - Показывать/скрывать колонки (перемещение между списками)
 * - Менять порядок видимых колонок (кнопки вверх/вниз)
 * - Искать колонки по названию
 * - Сохранять настройки в localStorage через useTableConfig
 */
export default function RepositoryTableColumnSettingsModal({
  opened,
  onClose,
  tableId,
  visible,
  hidden,
  onApply,
  onReset,
}: ColumnSettingsModalProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [leftIds, setLeftIds] = useState<string[]>(() => hidden.map(c => c.id));
  const [rightIds, setRightIds] = useState<string[]>(() => visible.map(c => c.id));

  // Sensors для drag-and-drop
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // Синхронизируем состояние при открытии модалки с пропсами
  useEffect(() => {
    if (opened) {
      setLeftIds(hidden.map(c => c.id));
      setRightIds(visible.map(c => c.id));
      setQuery(''); // Сбрасываем поиск при открытии
    }
  }, [opened, visible, hidden]);

  // Обработчик изменения порядка через drag-and-drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || String(active.id) === String(over.id)) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    setRightIds(items => {
      const oldIndex = items.indexOf(activeId);
      const newIndex = items.indexOf(overId);

      const newItems = [...items];
      const [removed] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, removed);

      return newItems;
    });
  };

  const indexById = useMemo(() => {
    const map = new Map<string, ColumnItem>();
    [...visible, ...hidden].forEach(c => map.set(c.id, c));
    return map;
  }, [visible, hidden]);

  const filteredLeft = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = leftIds
      .map(id => indexById.get(id))
      .filter((item): item is ColumnItem => item !== undefined);
    if (!q) return items;
    return items.filter(
      it => it.label.toLowerCase().includes(q) || it.id.toLowerCase().includes(q)
    );
  }, [leftIds, indexById, query]);

  const filteredRight = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = rightIds
      .map(id => indexById.get(id))
      .filter((item): item is ColumnItem => item !== undefined);
    if (!q) return items;
    return items.filter(
      it => it.label.toLowerCase().includes(q) || it.id.toLowerCase().includes(q)
    );
  }, [rightIds, indexById, query]);

  const moveToRight = (id: string) => {
    setLeftIds(prev => prev.filter(x => x !== id));
    setRightIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  const moveToLeft = (id: string) => {
    setRightIds(prev => prev.filter(x => x !== id));
    setLeftIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Колонки (${tableId})`}
      size='90%'
      centered
      zIndex={1000}
    >
      <Group align='flex-start' gap='md'>
        {/* Left: hidden */}
        <Box style={{ width: 200, flexShrink: 0 }}>
          <Text fw={600} mb='xs'>
            Не отображаются
          </Text>
          <ScrollArea style={{ height: 460 }}>
            <SimpleGrid cols={1} spacing={8} verticalSpacing={8}>
              {filteredLeft.map(c => (
                <Card
                  key={c.id}
                  p={6}
                  style={{ border: '1px solid var(--mantine-color-default-border)' }}
                >
                  <Group justify='space-between' gap={4} wrap='nowrap'>
                    <Text size='xs' truncate style={{ flex: 1, fontSize: 11 }}>
                      {c.label}
                    </Text>
                    <Tooltip label='Показать'>
                      <ActionIcon
                        size='xs'
                        variant='subtle'
                        onClick={() => moveToRight(c.id)}
                        aria-label='Показать'
                      >
                        <IconArrowRight size={12} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          </ScrollArea>
        </Box>

        {/* Right: visible */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} mb='xs'>
            Отображаются
          </Text>
          <ScrollArea style={{ height: 460 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={rightIds} strategy={rectSortingStrategy}>
                <SimpleGrid cols={4} spacing={8} verticalSpacing={8}>
                  {filteredRight.map(c => (
                    <SortableItem key={c.id} item={c} onMoveLeft={() => moveToLeft(c.id)} />
                  ))}
                </SimpleGrid>
              </SortableContext>
            </DndContext>
          </ScrollArea>
        </Box>
      </Group>

      <Group justify='space-between' mt='md'>
        <Button
          size='xs'
          variant='light'
          color='red'
          onClick={() => {
            const result = onReset();
            if (result) {
              setRightIds(result.visible);
              setLeftIds(result.hidden);
            }
          }}
        >
          Сбросить
        </Button>
        <Group>
          <Button size='xs' variant='subtle' onClick={onClose}>
            Отмена
          </Button>
          <Button size='xs' onClick={() => onApply(rightIds, leftIds)}>
            Сохранить
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
